import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft,
  Calendar,
  User,
  BookOpen,
  Tag,
  Eye,
  Edit,
  Trash2,
  Share2,
  Bookmark,
  Star,
  Download,
  Printer,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-toastify";
import { server } from "../server";

const NotesDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notesId } = location?.state || null;
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch note details from API
  const fetchNoteDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data } = await axios.get(`${server}/notes/getById`, {
        params: { notesId: notesId || 2 }, // Use params or default to 2
      });

      setNote(data.data);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to load note details";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNoteDetails();
  }, [notesId]);

  // Handle delete note
  const handleDeleteNote = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this note? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      setIsDeleting(true);
      await axios.delete(`${server}/notes/${notesId || 2}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      toast.success("Note deleted successfully");
      navigate("/notes"); // Redirect to notes list
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to delete note";
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle edit note
  const handleEditNote = () => {
    navigate(`/notes/edit/${notesId || 2}`);
  };

  // Handle share note
  const handleShareNote = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Note link copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy link to clipboard");
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="flex items-center gap-3"
            >
              <RefreshCw className="w-8 h-8 text-blue-400" />
              <span className="text-gray-300 text-lg">
                Loading note details...
              </span>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-700 text-center"
          >
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">
              Error Loading Note
            </h2>
            <p className="text-gray-400 mb-6">{error}</p>
            <div className="flex gap-4 justify-center">
              <motion.button
                onClick={fetchNoteDetails}
                className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <RefreshCw className="w-5 h-5" />
                Try Again
              </motion.button>
              <motion.button
                onClick={() => navigate("/notes")}
                className="flex items-center gap-2 px-6 py-3 border-2 border-gray-600 text-gray-300 rounded-xl hover:border-gray-500 hover:bg-gray-700 transition-colors duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Notes
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!note) {
    return (
      <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-700 text-center"
          >
            <BookOpen className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">
              Note Not Found
            </h2>
            <p className="text-gray-400 mb-6">
              The requested note could not be found.
            </p>
            <motion.button
              onClick={() => navigate("/notes")}
              className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors duration-200 mx-auto"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Notes
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8 print:bg-white print:py-4">
      <motion.div
        className="max-w-4xl mx-auto"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header with Back Button */}
        <motion.div
          className="flex items-center justify-between mb-8"
          variants={itemVariants}
        >
          <motion.button
            onClick={() => navigate("/notes")}
            className="flex items-center gap-2 px-4 py-3 border-2 border-gray-600 text-gray-300 rounded-xl hover:border-gray-500 hover:bg-gray-700 transition-all duration-200 print:hidden"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Notes
          </motion.button>

          <div className="flex items-center gap-3 print:hidden">
            <motion.button
              onClick={handleShareNote}
              className="flex items-center gap-2 px-4 py-3 bg-green-500/20 border-2 border-green-500/30 text-green-400 rounded-xl hover:bg-green-500/30 transition-all duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Share2 className="w-5 h-5" />
              Share
            </motion.button>
            <motion.button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-3 bg-purple-500/20 border-2 border-purple-500/30 text-purple-400 rounded-xl hover:bg-purple-500/30 transition-all duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Printer className="w-5 h-5" />
              Print
            </motion.button>
          </div>
        </motion.div>

        {/* Note Card */}
        <motion.div
          className="bg-gray-800 rounded-2xl shadow-lg border border-gray-700 overflow-hidden print:bg-white print:border-none print:shadow-none"
          variants={itemVariants}
        >
          {/* Note Header */}
          <div className="p-6 border-b border-gray-700 print:border-b-2 print:border-gray-300">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-3 print:text-black">
                  {note.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                      note.isPublished
                        ? "bg-green-500/20 text-green-300 border border-green-500/30"
                        : "bg-gray-600 text-gray-300 border border-gray-500"
                    } print:bg-gray-200 print:text-gray-700 print:border-gray-300`}
                  >
                    {note.isPublished ? (
                      <>
                        <Eye className="w-4 h-4" />
                        Published
                      </>
                    ) : (
                      <>
                        <Bookmark className="w-4 h-4" />
                        Draft
                      </>
                    )}
                  </span>

                  {note.isPublished && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm font-medium border border-yellow-500/30 print:bg-yellow-100 print:text-yellow-700 print:border-yellow-300">
                      <Star className="w-4 h-4 fill-current" />
                      Live
                    </span>
                  )}

                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium border border-blue-500/30 print:bg-blue-100 print:text-blue-700 print:border-blue-300">
                    ID: {note.id}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 print:hidden">
                <motion.button
                  onClick={handleEditNote}
                  className="p-3 bg-blue-500/20 border-2 border-blue-500/30 text-blue-400 rounded-xl hover:bg-blue-500/30 transition-all duration-200"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Edit Note"
                >
                  <Edit className="w-5 h-5" />
                </motion.button>
                <motion.button
                  onClick={handleDeleteNote}
                  disabled={isDeleting}
                  className="p-3 bg-red-500/20 border-2 border-red-500/30 text-red-400 rounded-xl hover:bg-red-500/30 transition-all duration-200 disabled:opacity-50"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Delete Note"
                >
                  {isDeleting ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <RefreshCw className="w-5 h-5" />
                    </motion.div>
                  ) : (
                    <Trash2 className="w-5 h-5" />
                  )}
                </motion.button>
              </div>
            </div>

            {/* Meta Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-400 print:text-gray-600">
                <BookOpen className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium text-white print:text-black">
                  Subject:
                </span>
                <span>{note.subject || "Not specified"}</span>
              </div>

              <div className="flex items-center gap-2 text-gray-400 print:text-gray-600">
                <Tag className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium text-white print:text-black">
                  Chapter:
                </span>
                <span>{note.chapter || "Not specified"}</span>
              </div>

              <div className="flex items-center gap-2 text-gray-400 print:text-gray-600">
                <Calendar className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium text-white print:text-black">
                  Created:
                </span>
                <span>{formatDate(note.createdAt)}</span>
              </div>

              <div className="flex items-center gap-2 text-gray-400 print:text-gray-600">
                <User className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium text-white print:text-black">
                  Author ID:
                </span>
                <span>{note.createdBy}</span>
              </div>
            </div>
          </div>

          {/* Note Content */}
          <div className="p-6 print:p-0">
            <div
              className="prose prose-invert max-w-none print:prose-black print:max-w-none"
              style={{
                color: "#f9fafb",
                fontFamily: "Inter, sans-serif",
                lineHeight: "1.7",
              }}
            >
              <div
                dangerouslySetInnerHTML={{ __html: note.content }}
                className="note-content text-gray-100 print:text-black print:bg-white"
                style={{
                  fontSize: "16px",
                  lineHeight: "1.7",
                }}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-700 bg-gray-750 print:border-t-2 print:border-gray-300 print:bg-gray-100">
            <div className="flex items-center justify-between text-sm text-gray-400 print:text-gray-600">
              <div className="flex items-center gap-4">
                <span>Last updated: {formatDate(note.createdAt)}</span>
              </div>
              <div className="print:hidden">
                <span className="text-gray-500">Note ID: {note.id}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Additional Actions */}
        <motion.div
          className="flex flex-wrap gap-4 mt-6 print:hidden"
          variants={itemVariants}
        >
          <motion.button
            onClick={() => navigate("/notes")}
            className="flex items-center gap-2 px-6 py-3 border-2 border-gray-600 text-gray-300 rounded-xl hover:border-gray-500 hover:bg-gray-700 transition-all duration-200"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft className="w-5 h-5" />
            Back to All Notes
          </motion.button>

          <motion.button
            onClick={handleEditNote}
            className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-200"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Edit className="w-5 h-5" />
            Edit Note
          </motion.button>

          <motion.button
            onClick={handleShareNote}
            className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all duration-200"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Share2 className="w-5 h-5" />
            Share Note
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          body {
            background: white !important;
            color: black !important;
          }
          .print\\:bg-white {
            background: white !important;
          }
          .print\\:text-black {
            color: black !important;
          }
          .print\\:border-none {
            border: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .note-content {
            color: black !important;
            background: white !important;
          }
          .prose-invert {
            color: black !important;
          }
          .prose-invert h1,
          .prose-invert h2,
          .prose-invert h3,
          .prose-invert h4,
          .prose-invert h5,
          .prose-invert h6 {
            color: black !important;
          }
          .prose-invert p {
            color: black !important;
          }
          .prose-invert strong {
            color: black !important;
          }
        }
      `}</style>
    </div>
  );
};

export default NotesDetails;
